import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Material UI
import { Collapse } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HomeIcon from '@mui/icons-material/Home';

import { ExpandLess, ExpandMore, Policy, Storage } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import TimelineIcon from '@mui/icons-material/Timeline';
import DialpadIcon from '@mui/icons-material/Dialpad';
import CloseIcon from '@mui/icons-material/Close';
import BallotIcon from '@mui/icons-material/Ballot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DescriptionIcon from '@mui/icons-material/Description';
import PageviewIcon from '@mui/icons-material/Pageview';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { ReactComponent as PosnetIcon } from '../icons/posnet.svg';

// Hooks
import useAuthorize from '../hooks/user/useAuthorize';
import useUser from '../hooks/user/useUser';

import { Permissions } from '../models/Roles';
import ModalNewSale from '../views/orders/create';

const today = dayjs().format('YYYY-MM-DD');

const MenuItems = [
    {
        id: 'Sales',
        title: 'Ventas',
        icon: MonetizationOnIcon,
        authorizedRoles: [Permissions.SalesView],
        subMenu: [
            {
                text: "Listado de ventas",
                path: "/orders?pageNumber=1&pageSize=10",
                icon: <ReceiptIcon color='primary' />,
                authorizedRoles: [Permissions.SalesView]
            },
            {
                text: "Comprobantes Internos",
                path: "/internalVouchers?pageNumber=1&pageSize=10",
                icon: <DescriptionIcon color='primary' />,
                authorizedRoles: [Permissions.SalesView]
            },
            {
                text: "Catalogo",
                path: "/catalogs?pageSize=20&page=1",
                icon: <PhotoLibraryIcon color='primary' />,
                authorizedRoles: [Permissions.SalesView]
            },
            {
                text: "Reposición de Venta",
                path: `/replacementUnits?pageNumber=1&pageSize=10&startDate=${dayjs().add(-7, 'day').format('YYYY-MM-DD')}&endDate=${today}`,
                icon: <AddShoppingCartIcon color='primary' />,
                authorizedRoles: [Permissions.SalesView]
            },
        ]
    },
    {
        id: 'Customers',
        title: 'Clientes',
        icon: AssignmentIndIcon,
        authorizedRoles: [Permissions.CustomerView],
        subMenu: [{
            text: "Listado de clientes",
            path: "/customers?pageNumber=1&pageSize=10&active=true",
            icon: <GroupIcon color='primary' />,
            authorizedRoles: [Permissions.CustomerView]
        }]
    },
    {
        id: 'Stock',
        title: 'Stock',
        icon: ArchiveIcon,
        authorizedRoles: [Permissions.StockView],
        subMenu: [
            {
                text: "Remitos",
                path: "/stocks/referrals?pageNumber=1&pageSize=10",
                icon: <ReceiptIcon color='primary' />,
                authorizedRoles: [Permissions.StockView]
            },
            {
                text: "Inventario",
                path: "/stocks/inventory?pageNumber=1&pageSize=10",
                icon: <PlaylistAddCheckIcon color='primary' />,
                authorizedRoles: [Permissions.StockView]
            },
            {
                text: 'Historial de Prod.',
                path: '/stocks/product-history',
                icon: <HistoryIcon color='primary' />,
                authorizedRoles: [Permissions.StockMovementsHistoryView]
            },
            {
                text: 'Movimientos',
                path: '/movements?pageNumber=1&pageSize=10',
                icon: <TimelineIcon color='primary' />,
                authorizedRoles: [Permissions.StockView]
            },
            {
                text: "Recepción mercadería",
                path: '/movements/reception',
                icon: <TrendingUpIcon color='primary' />,
                authorizedRoles: [Permissions.StockView]
            },
            {
                text: "Despacho mercadería",
                path: '/movements/egress',
                icon: <LocalShippingIcon color='primary' />,
                authorizedRoles: [Permissions.StockView]
            }
        ]
    },
    {
        id: 'CashRegisters',
        title: 'Cajas',
        icon: DialpadIcon,
        authorizedRoles: [Permissions.CashregisterView],
        subMenu: [
            {
                text: "Consulta de Caja",
                path: "/cashRegisters/summary",
                icon: <PageviewIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: "Cierres",
                path: "/cashRegisters/closures",
                icon: <CloseIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: "Cierres de Lote",
                path: "/cashRegisters/settlements",
                icon: <PosnetIcon style={{ width: '1.45rem', height: '1.45rem', fill: '#FB7498' }}  />,
                authorizedRoles: [Permissions.SettlementsView]
            },
            {
                text: "Arqueos",
                path: "/cashRegisters/archings",
                icon: <BallotIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: 'Movimientos',
                path: '/cashRegisters/movements',
                icon: <TimelineIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: 'Fondo Fijo',
                path: '/cashRegisters/imprestMovements',
                icon: <AccountBalanceWalletIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: 'Facturas',
                path: '/cashRegisters/invoices',
                icon: <ReceiptIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            },
            {
                text: 'Notas de crédito',
                path: '/cashRegisters/vouchers',
                icon: <ReceiptOutlinedIcon color='primary' />,
                authorizedRoles: [Permissions.CashregisterView]
            }
        ]
    },
    {
        id: 'Audits',
        title: 'Auditoría',
        icon: Policy,
        authorizedRoles: [Permissions.AuditorView],
        subMenu: [
            {
                text: "Controles de Inventario",
                path: "/stockControls",
                icon: <Storage color='primary' />,
                authorizedRoles: [Permissions.AuditorView],
            },
        ]

    }
]

const MenuItemsBlock = [
    {
        id: 'Home',
        title: 'Inicio',
        icon: HomeIcon,
        subMenu: null,
        path: '/',
        authorizedRoles: [Permissions.SalesView],
    }
]

const MenuItem = ({ id, title, icon: Icon, subMenu, path, authorizedRoles }) => {
    const [open, setOpen] = useState(false);
    const isAuthorized = useAuthorize();
    return (
        <Fragment key={id}>
            {isAuthorized(authorizedRoles) &&
                <ListItem
                    button
                    onClick={subMenu ? () => setOpen(!open) : null}
                    component={path ? Link : null}
                    to={path}
                >
                    <ListItemIcon title={title}>
                        <Icon style={{ fontSize: 30 }} />
                    </ListItemIcon>
                    <ListItemText primary={<b>{title}</b>} sx={(theme) => ({ color: theme.palette.text.primary })} />
                    {subMenu && (open ? <ExpandLess /> : <ExpandMore />)}
                </ListItem>}
            {
                subMenu &&
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List>
                        {
                            subMenu?.map((item, index) => (
                                isAuthorized(item.authorizedRoles) &&
                                <ListItem button key={`appbar-opt-${index}`} component={Link} to={item.path}>
                                    <ListItemIcon title={item.text}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.text} sx={(theme) => ({ color: theme.palette.text.primary })} />
                                </ListItem>
                            ))
                        }
                    </List>
                </Collapse>
            }
        </Fragment>
    )
}

const ItemMenu = ({ key, title, icon: Icon, path = null, component = Link, authorizedRoles, onClick }) => {
    const isAuthorized = useAuthorize();
    return (
        <>
            {
                isAuthorized(authorizedRoles ?? []) &&
                <ListItem
                    button
                    key={key}
                    onClick={onClick}
                    component={component}
                    to={path}
                >
                    <ListItemIcon title={title}>
                        <Icon style={{ fontSize: 30 }} />
                    </ListItemIcon>
                    <ListItemText primary={<b>{title}</b>} sx={(theme) => ({ color: theme.palette.text.primary })} />
                </ListItem>
            }
        </>
    )
}

const NewCartSale = ({ id, title, icon: Icon, authorizedRoles }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);

    return (
        <Fragment key={id}>
            {
                showCreateModal &&
                <ModalNewSale
                    open={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                />
            }
            <ItemMenu
                title={title}
                icon={Icon}
                onClick={() => setShowCreateModal(true)}
                component={Link}
                authorizedRoles={authorizedRoles}
            />
        </Fragment>
    )
}

const OrdersSearch = () => {
    const { business } = useUser();

    return (
        <>
            {
                business?.isFranchise &&
                <ItemMenu
                    title="Buscador de Ventas"
                    icon={SearchIcon}
                    path="/orders/search"
                    component={Link}
                    authorizedRoles={[Permissions.SalesView]}
                />
            }
        </>
    )
}

const Routes = ({ isFranchise = false}) =>
    <>
        <div style={{ marginTop: '1rem' }}>
            {MenuItemsBlock.map(item => <MenuItem key={item.id} {...item} />)}
            <NewCartSale
                id="newCard"
                title="Nueva venta"
                icon={AddShoppingCartIcon}
                authorizedRoles={[Permissions.SalesCreate]}
            />
            <OrdersSearch />
        </div>
        <div style={{ overflowY: 'auto', overflowX: 'inherit' }}>
            {MenuItems.map(item => <MenuItem key={item.id} {...item} />)}
        </div>
    </>

export default Routes;